(() => {
  if (window.__noahqPhotoMotion) return;
  window.__noahqPhotoMotion = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();
  const piles = new Set();
  const limit = 5;
  const shadow = '0 8px 22px rgb(36 42 34 / .16)';
  const pose = (photo, y = photo.y, angle = photo.angle, scale = .92, tilt = 0, depth = 0) =>
    `perspective(1800px) translate3d(${photo.x}px, ${y}px, ${depth}px) rotateX(${tilt}deg) rotateZ(${angle}deg) scale(${scale})`;

  function createPile(gallery, previous) {
    const viewer = gallery.elements.viewer;
    const layer = document.createElement('div');
    layer.className = 'nq-photo-layer';
    layer.setAttribute('aria-hidden', 'true');
    layer.inert = true;
    const state = {
      gallery, viewer, layer, photos: [], generation: 0, originalHeight: viewer.style.height,
      canvasHeight: viewer.getBoundingClientRect().height,
      animations: [], userScroll: false,
      cancelPending() {
        cancelAnimationFrame(this.frame);
        clearTimeout(this.timer);
      },
      finish(cancelPending = true) {
        if (cancelPending) this.cancelPending();
        this.animations.forEach((animation) => animation.cancel());
        this.animations = [];
        viewer.classList.remove('nq-photo-dropping');
      },
      clear() {
        this.generation++;
        this.finish();
        clearTimeout(this.swipeTimer);
        viewer.removeEventListener('pointerdown', this.pointer);
        viewer.removeEventListener('wheel', this.wheel);
        viewer.removeEventListener('slideChanged', this.swipe);
        viewer.removeEventListener('click', this.zoom);
        layer.remove();
        viewer.style.height = this.originalHeight;
        viewer.classList.remove('nq-photo-stacked');
        piles.delete(this);
        if (states.get(gallery) === this) states.delete(gallery);
      },
      add(image, mediaId, initial = false) {
        const node = document.createElement('img');
        node.src = image.currentSrc || image.src;
        node.alt = '';
        node.className = 'nq-photo-print';
        node.dataset.nqMediaId = mediaId;
        const ratio = image.naturalWidth / image.naturalHeight;
        const shape = Math.min(1, ratio, 1 / ratio);
        const photo = {
          node, ratio, shape,
          angle: initial ? 0 : (.7 + Math.random() * 2.5) * shape * (Math.random() < .5 ? -1 : 1),
          x: initial ? 0 : (Math.random() - .5) * 3,
          y: initial ? 0 : (Math.random() - .5) * 3,
        };
        node.style.transform = pose(photo);
        node.style.boxShadow = shadow;
        this.photos.push(photo);
        while (this.photos.length > limit) {
          const oldest = this.photos.shift();
          oldest.node.getAnimations().forEach((animation) => animation.cancel());
          oldest.node.remove();
        }
        this.layout();
        // Size the print before insertion to avoid mobile viewport expansion from its intrinsic size.
        layer.append(node);
        return photo;
      },
      layout() {
        const width = viewer.getBoundingClientRect().width;
        if (!width) return;
        const mobile = matchMedia('(max-width: 749px)').matches;
        const height = this.canvasHeight;
        const viewportLimit = mobile ? Math.max(240, Math.min(660, innerHeight - 240)) : 660;
        const maxHeight = Math.min(viewportLimit, Math.max(1, height - 16));
        this.photos.forEach((photo) => {
          photo.width = Math.min(width * .92, maxHeight * photo.ratio);
          photo.height = photo.width / photo.ratio;
        });
        // Keep the thumbnail row and the page at the same height as photos accumulate.
        viewer.style.height = `${height}px`;
        this.photos.forEach((photo) => {
          Object.assign(photo.node.style, {
            width: `${photo.width}px`, height: `${photo.height}px`,
            left: `${(width - photo.width) / 2}px`, top: `${(height - photo.height) / 2}px`,
          });
        });
      },
    };
    state.pointer = () => { state.userScroll = true; };
    state.zoom = (event) => {
      if (event.target.closest('button, a')) return;
      const rect = state.photos.at(-1).node.getBoundingClientRect();
      if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
        viewer.querySelector('.is-active .product__media-toggle')?.click();
      }
    };
    state.wheel = (event) => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) state.userScroll = true; };
    state.swipe = (event) => {
      if (!state.userScroll) return;
      clearTimeout(state.swipeTimer);
      const media = event.detail.currentElement;
      state.swipeTimer = setTimeout(() => {
        state.userScroll = false;
        if (gallery.isConnected && gallery.contains(media) && !media.classList.contains('is-active')) {
          gallery.setActiveMedia(media.dataset.mediaId, false);
        }
      }, 250);
    };
    viewer.addEventListener('pointerdown', state.pointer, { passive: true });
    viewer.addEventListener('wheel', state.wheel, { passive: true });
    viewer.addEventListener('slideChanged', state.swipe);
    viewer.addEventListener('click', state.zoom);
    states.set(gallery, state);
    piles.add(state);
    viewer.append(layer);
    viewer.classList.add('nq-photo-stacked');
    state.add(previous, previous.closest('[data-media-id]').dataset.mediaId, true);
    return state;
  }

  document.addEventListener('noahq:media:change', (event) => {
    const gallery = event.target;
    if (!gallery.closest('product-info[data-nq-product-layout]')) return;
    let state = states.get(gallery);
    if (state && (state.viewer !== gallery.elements.viewer || !state.layer.isConnected)) {
      state.clear();
      state = null;
    }
    const { previousMedia, activeMedia } = event.detail;
    const previous = previousMedia.querySelector('.product__modal-opener--image .product__media img');
    const incoming = activeMedia.querySelector('.product__modal-opener--image .product__media img');
    if (reduced.matches || !Element.prototype.animate || !incoming || (!state && !previous?.naturalWidth)) {
      state?.clear();
      return;
    }
    state ||= createPile(gallery, previous);
    state.cancelPending();
    state.userScroll = false;
    clearTimeout(state.swipeTimer);
    const generation = ++state.generation;
    const current = () => states.get(gallery) === state && state.generation === generation;
    const start = () => {
      if (!current()) return;
      clearTimeout(state.timer);
      if (!gallery.isConnected || !gallery.contains(activeMedia) || !activeMedia.classList.contains('is-active') || reduced.matches || !incoming.naturalWidth) {
        state.clear();
        return;
      }
      const photo = state.add(incoming, activeMedia.dataset.mediaId);
      state.viewer.classList.add('nq-photo-dropping');
      const animation = photo.node.animate([
        { transform: pose(photo, photo.y - 4, photo.angle * .2, .92, -4, 220), opacity: 0, boxShadow: '0 26px 42px rgb(36 42 34 / .2)', offset: 0, easing: 'cubic-bezier(.25,.8,.25,1)' },
        { opacity: 1, offset: .16 },
        { transform: pose(photo, photo.y, photo.angle + photo.shape * .06, .92, .3), boxShadow: shadow, offset: .82, easing: 'ease-out' },
        { transform: pose(photo), opacity: 1, boxShadow: shadow, offset: 1 },
      ], { duration: 760, fill: 'both' });
      state.animations.push(animation);
      const landed = () => {
        state.animations = state.animations.filter((item) => item !== animation);
        animation.cancel();
        if (states.get(gallery) === state && !state.animations.length) state.viewer.classList.remove('nq-photo-dropping');
      };
      animation.finished.then(landed, landed);
    };
    incoming.loading = 'eager';
    state.timer = setTimeout(() => { if (current()) state.clear(); }, 4000);
    const ready = incoming.decode ? incoming.decode() : Promise.resolve();
    ready.then(() => { if (current()) state.frame = requestAnimationFrame(start); }).catch(() => { if (current()) state.clear(); });
  });

  const clearAll = () => [...piles].forEach((state) => state.clear());
  reduced.addEventListener('change', () => { if (reduced.matches) clearAll(); });
  addEventListener('resize', () => [...piles].forEach((state) => {
    if (!state.gallery.isConnected || !state.layer.isConnected) return state.clear();
    state.finish(false);
    state.layout();
  }), { passive: true });
  addEventListener('pagehide', clearAll);
  document.addEventListener('shopify:section:unload', (event) => {
    [...piles].forEach((state) => { if (event.target.contains(state.gallery)) state.clear(); });
  });
  new MutationObserver(() => {
    [...piles].forEach((state) => { if (!state.gallery.isConnected || !state.layer.isConnected) state.clear(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
